// @flow
import { constants } from '@clubmed/utils';
import { getKidsCivilities } from '@clubmed/domains-service';

import {
  EMAIL_OPTIN,
  PHONE_OPTIN,
  SMS_OPTIN,
  PROFILING,
} from 'Utils/legal-information/legalInformationIds.constants';
import {
  getAdultsOptions,
  getChildrenOptions,
  getDurationOptions,
  type DurationOptions,
} from 'Store/utils/selectOptionsBuilders';
import { titleCase } from 'Components/utils/string/string.util';
import {
  MAX_FORM_ADULTS,
  MIN_FORM_ADULTS,
  MAX_FORM_KIDS,
  MIN_FORM_KIDS,
} from 'Components/widgets/prebooking-form/PrebookingForm.constants';

// Line props

export type LineProduct = {
  productId: string,
  sortedFirst?: boolean,
};

export type PrebookingFormLineProps = {|
  dateFormat: string,
  mandatoryFieldsInformation: string,
  login: {
    memberSpace: string,
    labelCTA: string,
  },
  stay: {
    title: string,
    productLabel: string,
    productPlaceholder: string,
    minAgeInformation: string,
    departureCityLabel: string,
    departureCityPlaceholder: string,
    departureDateLabel: string,
    departureDatePlaceholder: string,
    durationLabel: string,
    durationValue: PluralizedLabel,
    durationPlaceholder: string,
    accommodationsLabel: string,
    accommodationsCategoryPlaceholder: string,
    accommodationsTypePlaceholder: string,
    numberOfParticipantsLabel: string,
    adultsAge: PluralizedLabel,
    adultsInformation: string,
    childrenAge: PluralizedLabel,
    childrenInformation: string,
    tooManyPax: {
      title: string,
      text: string,
    },
  },
  mainTraveler: {
    title: string,
    mainTravelerInformation: string,
    civilityPlaceholder: string,
    firstNamePlaceholder: string,
    firstNameUnicodePlaceholder: string,
    lastNamePlaceholder: string,
    lastNameUnicodePlaceholder: string,
    birthDateInformation: string,
    gmNumberPlaceholder: string,
    gmNumberInformation: string,
    emailPlaceholder: string,
    emailInformation: string,
    phoneNumberPlaceholder: string,
    phoneNumberInformation: string,
    favoriteTravelAgencyLabel: string,
    favoriteTravelAgencyPlaceholder: string,
    optinCommunicationsInformation: string,
    optinEmailLabel: string,
    optinSMSLabel: string,
  },
  pax: {
    title: string,
    adultCompanionLabel: string,
    childCompanionLabel: string,
  },
  sendRequest: string,
  products: Array<LineProduct>,
  departureDateStart: string,
  departureDateEnd: string,
|};

// Resolver props

export type TravelAgency = {
  id: string,
  name: string,
  address?: { city: string },
};

type BookingCriteria = {
  durations: Array<number>,
  departureCities: Array<{
    name: string,
    value: string,
  }>,
};

type ResortAccommodation = {
  id: string,
  label: string,
  categories: Array<{
    id: string,
    label: string,
  }>,
};

type ResortOpeningDates = Array<{
  opening: string,
  closing: string,
}>;

type Resort = {
  id: string,
  name: string,
  destination: { country: string },
  accommodations: Array<ResortAccommodation>,
  openingDates: ResortOpeningDates,
  adultOnly: boolean,
  minAuthorizedAge: {
    formattedValue: string,
    value: number,
  },
};

type AccommodationRanking = $Values<typeof constants.RANKINGS> | 'UNKNOWN';

type AccommodationCategory = {
  productId: string,
  accommodations: Array<{
    id: string,
    label: string,
    ranking: AccommodationRanking,
  }>,
};

export type LegalInformation = {
  id: string,
  title: string,
  description: string,
};

export type PrebookingFormResolverProps = {
  config: {
    memberSpace: { accessButtonLoggedin: string },
  },
  success: null | { url: string },
  error: null | { url: string },
  civilities: {
    adult: Array<{ name: string, value: string }>,
  },
  travelAgencies: null | Array<TravelAgency>,
  bookingsCriteria: Array<BookingCriteria>,
  resorts: Array<Resort>,
  accommodationCategories: Array<AccommodationCategory>,
  legalInformations: {
    newsletterOptins: Array<LegalInformation>,
  },
};

// PrebookingForm props

export type FormFieldOption = {
  name: string,
  value: string,
};

type FormProductAccommodation = {
  name: string,
  value: string,
  rankingIndex: number,
  types: Array<FormFieldOption>,
};

type FormProduct = {
  accommodations: Array<FormProductAccommodation>,
  adultOnly: boolean,
  destination: { country: string },
  durations: DurationOptions,
  minAuthorizedAge: string,
  minAuthorizedAgeInMonths: number,
  name: string,
  openingDates: ResortOpeningDates,
  productId: string,
  transports: Array<FormFieldOption>,
};

export type FormProducts = {
  [string]: FormProduct,
};

// CIVILITIES

export const getCivilities = (adultCivilities: Array<FormFieldOption>, locale: string) => ({
  adults: adultCivilities,
  kids: getKidsCivilities(locale).map(({ value, label }) => ({ value, name: label })),
});

// TRAVEL AGENCY OPTIONS

const getAgencyName = (travelAgency: TravelAgency) => {
  return `${travelAgency.address?.city || ''} - ${travelAgency.name}`;
};

const getFilteredAgencyName = (travelAgency: TravelAgency) => {
  /**
   * ^[.+?\s]*            Match 0 or more chars or white space at the beginning
   * (?:<T1>|<T2>)?       Do not capture group of 1st or 2nd potential terms
   * \s*                  Match 0 or more white spaces
   * (.+?)$               Capture group of any chars up to the end
   * /i                   Case insensitive Flags
   */
  const agencyNameFilter = /^[.+?\s]*(?:CLUB MED VOYAGES|APPARTEMENT BOUTIQUE)?\s*(.+?)$/i;
  const trimmedName = agencyNameFilter.exec(travelAgency.name);
  return trimmedName && trimmedName[1] ? trimmedName[1] : getAgencyName(travelAgency);
};

export const getTravelAgencyOptions = (
  travelAgencies: null | Array<TravelAgency>,
  locale: string,
): Array<FormFieldOption> => {
  if (!travelAgencies) {
    return [];
  }

  return travelAgencies
    .map(travelAgency => ({
      name: locale === 'fr-FR' ? getFilteredAgencyName(travelAgency) : getAgencyName(travelAgency),
      value: travelAgency.id,
    }))
    .sort(
      (travelAgencyA, travelAgencyB) =>
        travelAgencyA.name.localeCompare(travelAgencyB.name) > 0 ? 1 : -1,
    );
};

// ADULTS/KIDS NUMBER OPTIONS

export const getAdultsNumberOptions = (adultsAge: PluralizedLabel) =>
  getAdultsOptions(adultsAge, MIN_FORM_ADULTS, MAX_FORM_ADULTS).map<FormFieldOption>(
    ({ label, value }) => ({
      name: label,
      value: value.toString(),
    }),
  );

export const getKidsNumberOptions = (kidsAge: PluralizedLabel) =>
  getChildrenOptions(kidsAge, MIN_FORM_KIDS, MAX_FORM_KIDS).map<FormFieldOption>(
    ({ label, value }) => ({
      name: label,
      value: value.toString(),
    }),
  );

// NEWSLETTER OPTINS

const getNewsletterOptin = (optin: void | LegalInformation) => {
  return optin
    ? {
        title: optin.title,
        description: optin.description,
      }
    : null;
};

export const getNewsletterOptins = (newsletterOptins: Array<LegalInformation>) => {
  const optinEmail = newsletterOptins.find(optin => optin.id === EMAIL_OPTIN);
  const optinPhone = newsletterOptins.find(optin => optin.id === PHONE_OPTIN);
  const optinSms = newsletterOptins.find(optin => optin.id === SMS_OPTIN);
  const optinProfile = newsletterOptins.find(optin => optin.id === PROFILING);
  return {
    optinEmail: getNewsletterOptin(optinEmail),
    optinPhone: getNewsletterOptin(optinPhone),
    optinSms: getNewsletterOptin(optinSms),
    optinProfile: getNewsletterOptin(optinProfile),
  };
};

// PRODUCT OPTIONS

export type ProductOption = {
  key: string,
  name: string,
  value: null | string,
  isDisabled?: boolean,
};

const getProductOption = (isTopProduct: boolean) => (product: FormProduct) => ({
  key: `${product.productId}-${isTopProduct ? 'top' : 'regular'}`,
  name: `${product.name}, ${titleCase(product.destination.country)}`,
  value: product.productId,
});

export function getProductOptions(
  lineProducts: Array<LineProduct>,
  products: FormProducts,
): Array<ProductOption> {
  const topProducts = lineProducts
    .filter(lineProduct => lineProduct.sortedFirst)
    .map<FormProduct>(({ productId }) => products[productId])
    .filter(Boolean)
    .map<ProductOption>(getProductOption(true));

  return [
    ...topProducts,
    ...(topProducts.length
      ? [{ key: 'separator', name: '──────────', value: null, isDisabled: true }]
      : []),
    // https://davidwalsh.name/flow-object-values
    ...(Object.values(products): any)
      .map<ProductOption>(getProductOption(false))
      .sort((productOptionA, productOptionB) => {
        if (!productOptionA.name) return 1;
        if (!productOptionB.name) return -1;
        return productOptionA.name.localeCompare(productOptionB.name);
      }),
  ];
}

// PRODUCT DATA

function getAccommodationsOptions(
  accommodations: Array<ResortAccommodation> = [],
  accommodationCategories: Array<{
    id: string,
    label: string,
    ranking: AccommodationRanking,
  }> = [],
): Array<FormProductAccommodation> {
  return accommodationCategories
    .map(accommodationCategory => {
      const {
        id: categoryId,
        label: categoryLabel,
        ranking: categoryRanking,
      } = accommodationCategory;
      const types = accommodations
        .filter(accommodation => accommodation.categories.map(({ id }) => id).includes(categoryId))
        .map(({ id, label }) => ({ name: label, value: id }));
      return {
        value: categoryId,
        name: categoryLabel,
        rankingIndex: Object.values(constants.RANKINGS).findIndex(
          ranking => ranking === categoryRanking,
        ),
        types,
      };
    })
    .sort(
      (accommodationsOptionA, accommodationsOptionB) =>
        accommodationsOptionB.rankingIndex - accommodationsOptionA.rankingIndex,
    );
}

type ProductInputs = {
  resorts: Array<Resort>,
  bookingsCriteria: Array<BookingCriteria>,
  accommodationCategories: Array<AccommodationCategory>,
  durationLabels: PluralizedLabel,
};

export function getFormProducts({
  resorts,
  bookingsCriteria,
  accommodationCategories,
  durationLabels,
}: ProductInputs) {
  return resorts.reduce((products, product, productIndex) => {
    const {
      name,
      destination,
      adultOnly,
      openingDates,
      accommodations,
      minAuthorizedAge,
    } = product;

    const durations: Array<number> = bookingsCriteria[productIndex]?.durations || [];
    const transports: Array<{ value: string, name: string }> =
      bookingsCriteria[productIndex]?.departureCities || [];
    const productAccommodationCategories = accommodationCategories.find(
      category => category.productId === product.id,
    );
    const categories: Array<{
      id: string,
      label: string,
      ranking: AccommodationRanking,
    }> = productAccommodationCategories?.accommodations || [];

    products[product.id] = {
      productId: product.id,
      name,
      destination,
      openingDates,
      minAuthorizedAge: minAuthorizedAge.formattedValue || '',
      minAuthorizedAgeInMonths: minAuthorizedAge.value || '',
      durations: getDurationOptions(durationLabels, durations, 'count'),
      transports,
      accommodations: getAccommodationsOptions(accommodations, categories),
      adultOnly,
    };

    return products;
  }, {});
}
